import { useCallback, useContext, useState } from 'react'
import * as Yup from 'yup'
import 'yup-phone-lite'
import { Formik } from 'formik'
import {
    MidmarkButton,
    MidmarkFormikTextField,
    MidmarkInlineNotification,
    VisibilityIcon,
    VisibilityOffIcon,
    ErrorIcon,
    InformationIcon,
    MidmarkLink,
} from '@midmark-enterprise/shared-components'
import IconButton from '@mui/material/IconButton'
import { SignUpScrolledFormLayout } from '@/components/Shared/SignUpScrolledFormLayout'
import { UsersRegistrationDataContext } from '../user-signup-modal.component'
import { useTranslation } from 'react-i18next'
import { MenuItem } from '@mui/material'
import { UsersRegistrationService } from '@midmark/users-registration-service'

const oneSpecialCharacterReg = new RegExp(
    /^(?=.*[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]).{1,}$/,
)
const oneLetterReg = new RegExp(/[a-z,A-Z]/)
const validEmailReg = new RegExp(
    /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
)

export const scrollToErrorElement = (
    testId: string,
    conatinerTestId: string,
): boolean => {
    const errorElement = document.querySelector(`[data-testid="${testId}"]`)
    if (!errorElement) {
        return false
    }

    const containerElement = document.querySelector(
        `[data-testid="${conatinerTestId}"]`,
    )

    // If Container exists, scroll container
    if (containerElement) {
        containerElement.scrollTo({
            top: 0,
            behavior: 'smooth',
        })

        return true
    }

    // Fall back to default scrolling if container not specified or not found
    errorElement.scrollIntoView({ behavior: 'smooth' })
    return true
}

export const buildSchema = (t: (key: string) => string) => {
    const VALID_PREFERRED_CONTACT_OPTIONS = ['Email', 'Mobile', 'Work']
    return Yup.object().shape({
        firstName: Yup.string().required(
            t('userSignUpStepper.steps.signUpForm.requiredFirstName'),
        ),
        lastName: Yup.string().required(
            t('userSignUpStepper.steps.signUpForm.requiredLastName'),
        ),
        email: Yup.string()
            .email()
            .matches(
                validEmailReg,
                t('userSignUpStepper.steps.signUpForm.invalidEmail'),
            )
            .required(t('userSignUpStepper.steps.signUpForm.requiredEmail')),
        password: Yup.string()
            .required(t('userSignUpStepper.steps.signUpForm.passwordRequired'))
            .min(
                10,
                t('userSignUpStepper.steps.signUpForm.passwordMatchCriteria'),
            )
            .matches(
                /\d/,
                t('userSignUpStepper.steps.signUpForm.passwordMatchNumbers'),
            )
            .matches(
                oneLetterReg,
                t('userSignUpStepper.steps.signUpForm.passwordMatchOneLetter'),
            )
            .matches(
                oneSpecialCharacterReg,
                t(
                    'userSignUpStepper.steps.signUpForm.passwordMatchSpecialCharacter',
                ),
            )
            .oneOf(
                [Yup.ref('confirmPassword')],
                t('userSignUpStepper.steps.signUpForm.passwordsMustMatch'),
            ),
        confirmPassword: Yup.string().required(
            t(
                'userSignUpStepper.steps.signUpForm.passwordConfirmPasswordIsRequired',
            ),
        ),
        jobTitle: Yup.string().required(
            t(
                'userSignUpStepper.steps.signUpForm.userProfile.jobTitleRequiredErrorMessage',
            ),
        ),
        workPhone: Yup.string().phone(
            'US',
            t(
                'userSignUpStepper.steps.signUpForm.userProfile.phoneNumberInvalidErrorMessage',
            ),
        ),
        workExtension: Yup.string(),
        mobilePhone: Yup.string().phone(
            'US',
            t(
                'userSignUpStepper.steps.signUpForm.userProfile.phoneNumberInvalidErrorMessage',
            ),
        ),
        preferedContactMethod: Yup.mixed().oneOf(
            VALID_PREFERRED_CONTACT_OPTIONS,
            t(
                'userSignUpStepper.steps.signUpForm.userProfile.preferredContactMethodInvalidOptionErrorMessage',
            ),
        ),
    })
}

export interface SignUpFormProps {
    onSubmit: () => void
    redirectUri: string
}

export default function SignUpForm({
    onSubmit,
    redirectUri,
}: SignUpFormProps): JSX.Element {
    const { t } = useTranslation()
    const [passwordVisible, setPasswordVisible] = useState(false)
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)
    const [showErrorNotification, setShowErrorNotification] = useState(false)
    const [errorNotificationText, setErrorNotificationText] = useState('')
    const [showPasswordResetLink, setShowPasswordResetLink] = useState(false)
    const [passwordResetLink, setPasswordResetLink] = useState('')
    const [preferredContactMethod, SetPreferredContactMethod] = useState<
        PreferredContactMethod | undefined
    >(undefined)
    const validateEmailMutation =
        UsersRegistrationService.useValidateEmailMutation()

    const usersRegistrationData = useContext(UsersRegistrationDataContext)

    const togglePasswordVisibility = useCallback(() => {
        setPasswordVisible(!passwordVisible)
    }, [passwordVisible])

    const toggleConfirmPasswordVisibility = useCallback(() => {
        setConfirmPasswordVisible(!confirmPasswordVisible)
    }, [confirmPasswordVisible])

    const validate = (values: UsersRegistrationFormData) => {
        if (
            values.firstName.trim() === '' ||
            values.lastName.trim() === '' ||
            values.email.trim() === '' ||
            values.jobTitle.trim() === ''
        ) {
            setShowErrorNotification(true)
            setErrorNotificationText(
                t('userSignUpStepper.steps.signUpForm.completeRequiredFields'),
            )
            scrollToError()
        }
        SetPreferredContactMethod(values.preferredContactMethod)
    }

    const scrollToError = () => {
        // Target the scrollable container with the proper selector
        scrollToErrorElement(
            DATA_TESTIDS.errorNotification,
            DATA_TESTIDS.signUpFormContainer,
        )
    }

    const handleSubmit = (
        userData: UsersRegistrationFormData,
        actions: any,
    ): void => {
        if (usersRegistrationData) {
            const {
                setUserRegistrationData,
                setInitialEmailVerificationCodeSent,
            } = usersRegistrationData
            setUserRegistrationData(userData)
            setInitialEmailVerificationCodeSent(false)
        }

        validateEmailMutation
            .mutateAsync({
                emailAddress: userData.email,
                reCaptchaId: '',
            })
            .then((res) => {
                if (res.isValid) {
                    setShowErrorNotification(false)
                    setShowPasswordResetLink(false)
                    setPasswordResetLink('')
                    onSubmit()
                } else {
                    switch (res.errorCode) {
                        case 600:
                            setShowErrorNotification(true)
                            setErrorNotificationText(
                                t(
                                    'userSignUpStepper.steps.signUpForm.emailAlreadyExists',
                                ),
                            )
                            setShowPasswordResetLink(true)
                            setPasswordResetLink(
                                import.meta.env.VITE_RESET_PASSWORD_URL +
                                    '?email=' +
                                    userData.email,
                            )
                            scrollToError()
                            break
                        case 601:
                        case 602:
                            setShowErrorNotification(true)
                            setErrorNotificationText(
                                t(
                                    `userSignUpStepper.steps.signUpForm.emailValidation${res.errorCode}Error`,
                                ),
                            )
                            setShowPasswordResetLink(false)
                            setPasswordResetLink('')
                            scrollToError()
                            break
                    }
                    actions.setSubmitting(false) // Stop the form from submitting
                }
            })
            .catch((error: Error) => {
                console.error(error)
            })
    }

    return (
        <div style={{ height: '100%' }}>
            <Formik
                initialValues={{
                    email:
                        usersRegistrationData?.userRegistrationData?.email ??
                        '',
                    firstName:
                        usersRegistrationData?.userRegistrationData
                            ?.firstName ?? '',
                    lastName:
                        usersRegistrationData?.userRegistrationData?.lastName ??
                        '',
                    password:
                        usersRegistrationData?.userRegistrationData?.password ??
                        '',
                    confirmPassword:
                        usersRegistrationData?.userRegistrationData
                            ?.confirmPassword ?? '',
                    jobTitle:
                        usersRegistrationData?.userRegistrationData?.jobTitle ??
                        '',
                    workPhone:
                        usersRegistrationData?.userRegistrationData
                            ?.workPhone ?? '',
                    workExtension:
                        usersRegistrationData?.userRegistrationData
                            ?.workExtension ?? '',
                    mobilePhone:
                        usersRegistrationData?.userRegistrationData
                            ?.mobilePhone ?? '',
                    preferredContactMethod:
                        usersRegistrationData?.userRegistrationData
                            ?.preferredContactMethod ?? undefined,
                }}
                validationSchema={buildSchema(t)}
                validateOnChange={true}
                validateOnBlur={true}
                validate={validate}
                onSubmit={(values, actions) =>
                    handleSubmit(values, actions)
                }
            >
                {(props) => (
                    <SignUpScrolledFormLayout
                        title={t('userSignUpStepper.steps.signUpForm.title')}
                        description={t(
                            'userSignUpStepper.steps.signUpForm.description',
                        )}
                        buttons={
                            <>
                                <MidmarkButton
                                    type="back"
                                    variant="outlined"
                                    href={redirectUri}
                                    data-testid={
                                        DATA_TESTIDS.iAlreadyHaveALoginLink
                                    }
                                >
                                    {t(
                                        'userSignUpStepper.steps.signUpForm.iAlreadyHaveALoginLink',
                                    )}
                                </MidmarkButton>
                                <MidmarkButton
                                    type="submit"
                                    isSubmitting={props.isSubmitting}
                                    data-testid={DATA_TESTIDS.signupButton}
                                    style={{ width: '140px' }}
                                >
                                    {t(
                                        'userSignUpStepper.steps.signUpForm.signUpButton',
                                    )}
                                </MidmarkButton>
                            </>
                        }
                    >
                        <div
                            data-testid={DATA_TESTIDS.signUpFormContainer}
                            style={{
                                height: `calc(100vh - 260px)`,
                                overflowY: 'scroll',
                                paddingRight: '32px',
                                scrollbarColor: ' #E7E7E7' + ' transparent',
                                scrollbarGutter: '8px',
                            }}
                        >
                            <div
                                style={{
                                    borderBottom:
                                        '2px var(--General-divider, #E7E7E7) solid',
                                    paddingBottom: '12px',
                                    gap: '12px',
                                }}
                            >
                                {showErrorNotification && (
                                    <MidmarkInlineNotification
                                        sx={{
                                            mt: 1,
                                            height: '48px',
                                            fontSize: '34px',
                                            padding: '0px 8px 0px 8px',
                                            '& .MuiAlert-root': {
                                                paddingLeft: '4px',
                                            },
                                            '& .MuiAlert-icon': {
                                                marginLeft: '0px',
                                            },
                                            '& .MuiAlert-message': {
                                                paddingTop: '0px',
                                                paddingBottom: '0px',
                                                fontSize: '14px',
                                            },
                                            '& .MuiAlert-action': {
                                                p: 0,
                                            },
                                        }}
                                        severity={'error'}
                                        icon={<ErrorIcon />}
                                        data-testid={
                                            DATA_TESTIDS.errorNotification
                                        }
                                        forceOpen={true}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'flex-start',
                                                gap: '4px',
                                                paddingRight: '16px',
                                            }}
                                        >
                                            {errorNotificationText}
                                            {showPasswordResetLink && (
                                                <MidmarkLink
                                                    component={'button'}
                                                    onClick={() => {
                                                        window.open(
                                                            passwordResetLink,
                                                            '_self',
                                                        )
                                                    }}
                                                    data-testid={
                                                        DATA_TESTIDS.resetPasswordLink
                                                    }
                                                    size="small"
                                                    underline="none"
                                                    textAlign={'left'}
                                                >
                                                    {t(
                                                        'userSignUpStepper.steps.signUpForm.resetPasswordLink',
                                                    )}
                                                </MidmarkLink>
                                            )}
                                        </div>
                                    </MidmarkInlineNotification>
                                )}

                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.firstNameLabel',
                                            )}{' '}
                                            <span style={{ color: 'red' }}>
                                                *
                                            </span>
                                        </span>
                                    }
                                    autoComplete="firstname"
                                    data-testid={
                                        DATA_TESTIDS.firstNameTextField
                                    }
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.firstNamePlaceholder',
                                    )}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.firstNameTextFieldInput,
                                    }}
                                    formikProps={props}
                                    name="firstName"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.lastNameLabel',
                                            )}{' '}
                                            <span style={{ color: 'red' }}>
                                                *
                                            </span>
                                        </span>
                                    }
                                    autoComplete="lastname"
                                    data-testid={DATA_TESTIDS.lastNameTextField}
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.lastNamePlaceholder',
                                    )}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.lastNameTextFieldInput,
                                    }}
                                    formikProps={props}
                                    name="lastName"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.jobTitleLabel',
                                            )}{' '}
                                            <span style={{ color: 'red' }}>
                                                *
                                            </span>
                                        </span>
                                    }
                                    autoComplete="jobTitle"
                                    data-testid={DATA_TESTIDS.jobTitleTextField}
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.jobTitlePlaceholder',
                                    )}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.jobTitleTextFieldInput,
                                    }}
                                    formikProps={props}
                                    name="jobTitle"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.businessEmailAddressLabel',
                                            )}{' '}
                                            <span style={{ color: 'red' }}>
                                                *
                                            </span>
                                        </span>
                                    }
                                    autoComplete="email"
                                    data-testid={DATA_TESTIDS.emailTextField}
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.emailPlaceholder',
                                    )}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.emailTextFieldInput,
                                    }}
                                    formikProps={props}
                                    name="email"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.workPhoneLabel',
                                            )}{' '}
                                        </span>
                                    }
                                    autoComplete="workPhone"
                                    data-testid={
                                        DATA_TESTIDS.workPhoneTextField
                                    }
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.workPhonePlaceholder',
                                    )}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.workPhoneTextFieldInput,
                                    }}
                                    formikProps={props}
                                    name="workPhone"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.workExtensionLabel',
                                            )}{' '}
                                        </span>
                                    }
                                    autoComplete="workExtension"
                                    data-testid={
                                        DATA_TESTIDS.workExtensionTextField
                                    }
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.workExtensionPlaceholder',
                                    )}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.workExtensionTextFieldInput,
                                    }}
                                    formikProps={props}
                                    name="workExtension"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.mobilePhoneLabel',
                                            )}{' '}
                                        </span>
                                    }
                                    autoComplete="mobilePhone"
                                    data-testid={
                                        DATA_TESTIDS.mobilePhoneTextField
                                    }
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.mobilePhonePlaceholder',
                                    )}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.mobilePhoneTextFieldInput,
                                    }}
                                    formikProps={props}
                                    name="mobilePhone"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    data-testid={
                                        DATA_TESTIDS.prefferedContactTextFieldInput
                                    } // Fix the `data-testid` here
                                    formikProps={props}
                                    name="preferredContactMethod"
                                    select
                                    fullWidth={true}
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.userProfile.preferredContactMethodPlaceholder',
                                    )}
                                    label={t(
                                        'userSignUpStepper.steps.signUpForm.userProfile.preferredContactMethodLabel',
                                    )}
                                    InputProps={{
                                        sx: {
                                            height: '45.6px',
                                            '& .MidmarkTextField-selectInput[aria-expanded="true"]':
                                                {
                                                    fontWeight: 'bold',
                                                },
                                            '& .MidmarkTextField-inputSelectPlaceholder':
                                                {
                                                    color: '#b3b3b3',
                                                    fontSize: '14px',
                                                    fontWeight:
                                                        'normal !important',
                                                },
                                            '& .MidmarkTextField-selectIcon': {
                                                color: '#A3A3A3',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem
                                        value="Email"
                                        data-testid={
                                            DATA_TESTIDS.preferredContactMethodEmail
                                        }
                                        sx={{
                                            height: '45.6px',
                                            fontWeight:
                                                preferredContactMethod ===
                                                'Email'
                                                    ? 'bold'
                                                    : 'normal',
                                        }}
                                    >
                                        {t(
                                            'userSignUpStepper.steps.signUpForm.userProfile.preferredContactMethods.email',
                                        )}
                                    </MenuItem>
                                    <MenuItem
                                        value="Mobile"
                                        data-testid={
                                            DATA_TESTIDS.preferredContactMethodMobile
                                        }
                                        sx={{
                                            height: '45.6px',
                                            fontWeight:
                                                preferredContactMethod ===
                                                'Mobile'
                                                    ? 'bold'
                                                    : 'normal',
                                        }}
                                    >
                                        {t(
                                            'userSignUpStepper.steps.signUpForm.userProfile.preferredContactMethods.mobile',
                                        )}
                                    </MenuItem>
                                    <MenuItem
                                        value="Work"
                                        data-testid={
                                            DATA_TESTIDS.preferredContactMethodWork
                                        }
                                        sx={{
                                            height: '45.6px',
                                            fontWeight:
                                                preferredContactMethod ===
                                                'Work'
                                                    ? 'bold'
                                                    : 'normal',
                                        }}
                                    >
                                        {t(
                                            'userSignUpStepper.steps.signUpForm.userProfile.preferredContactMethods.work',
                                        )}
                                    </MenuItem>
                                </MidmarkFormikTextField>
                            </div>
                            <div
                                style={{
                                    gap: '12px',
                                    paddingTop: '12px',
                                    paddingBottom: '12px',
                                }}
                            >
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.passwordLabel',
                                            )}{' '}
                                            <span style={{ color: 'red' }}>
                                                *
                                            </span>
                                        </span>
                                    }
                                    autoComplete="password"
                                    data-testid={DATA_TESTIDS.passwordTextField}
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.passwordPlaceholder',
                                    )}
                                    type={passwordVisible ? 'text' : 'password'}
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.passwordTextFieldInput,
                                    }}
                                    InputProps={{
                                        endAdornment: props.values.password &&
                                            props.values.password.length >
                                                0 && (
                                                <IconButton
                                                    data-testid={
                                                        DATA_TESTIDS.togglePasswordVisibilityButton
                                                    }
                                                    sx={{ p: 0 }}
                                                    onClick={
                                                        togglePasswordVisibility
                                                    }
                                                >
                                                    {passwordVisible ? (
                                                        <VisibilityIcon />
                                                    ) : (
                                                        <VisibilityOffIcon />
                                                    )}
                                                </IconButton>
                                            ),
                                    }}
                                    formikProps={props}
                                    name="password"
                                    fullWidth={true}
                                />
                                <MidmarkFormikTextField
                                    label={
                                        <span>
                                            {t(
                                                'userSignUpStepper.steps.signUpForm.confirmPasswordLabel',
                                            )}{' '}
                                            <span style={{ color: 'red' }}>
                                                *
                                            </span>
                                        </span>
                                    }
                                    autoComplete="password"
                                    data-testid={
                                        DATA_TESTIDS.confirmPasswordTextField
                                    }
                                    placeholder={t(
                                        'userSignUpStepper.steps.signUpForm.confirmPasswordPlaceholder',
                                    )}
                                    type={
                                        confirmPasswordVisible
                                            ? 'text'
                                            : 'password'
                                    }
                                    inputProps={{
                                        'data-testid':
                                            DATA_TESTIDS.confirmPasswordTextFieldInput,
                                    }}
                                    InputProps={{
                                        endAdornment: props.values
                                            .confirmPassword &&
                                            props.values.confirmPassword
                                                .length > 0 && (
                                                <IconButton
                                                    sx={{ p: 0 }}
                                                    data-testid={
                                                        DATA_TESTIDS.toggleConfirmPasswordVisibilityButton
                                                    }
                                                    onClick={
                                                        toggleConfirmPasswordVisibility
                                                    }
                                                >
                                                    {confirmPasswordVisible ? (
                                                        <VisibilityIcon />
                                                    ) : (
                                                        <VisibilityOffIcon />
                                                    )}
                                                </IconButton>
                                            ),
                                    }}
                                    formikProps={props}
                                    name="confirmPassword"
                                    fullWidth={true}
                                />
                                <MidmarkInlineNotification
                                    sx={{
                                        mt: 1,
                                        '& .MuiAlert-root': {
                                            paddingLeft: '4px',
                                        },
                                        '& .MuiAlert-icon': {
                                            marginLeft: '0px',
                                        },
                                        '& .MuiAlert-message': {
                                            paddingTop: '8px',
                                            paddingBottom: '8px',
                                            fontSize: '14px',
                                        },
                                        '& .MuiAlert-action': {
                                            p: 0,
                                        },
                                    }}
                                    severity={'info'}
                                    icon={<InformationIcon />}
                                    data-testid={DATA_TESTIDS.infoNotification}
                                    forceOpen={true}
                                >
                                    {t(
                                        'userSignUpStepper.steps.signUpForm.passwordRequirements',
                                    )}
                                </MidmarkInlineNotification>
                            </div>
                        </div>
                    </SignUpScrolledFormLayout>
                )}
            </Formik>
        </div>
    )
}

export const DATA_TESTIDS = {
    firstNameTextField: 'first-name-text-field',
    firstNameTextFieldInput: 'first-name-text-field-input',
    lastNameTextField: 'last-name-text-field',
    lastNameTextFieldInput: 'last-name-text-field-input',
    emailTextField: 'email-text-field',
    emailTextFieldInput: 'email-text-field-input',
    passwordTextField: 'password-text-field',
    passwordTextFieldInput: 'password-text-field-input',
    confirmPasswordTextField: 'confirm-password-text-field',
    confirmPasswordTextFieldInput: 'confirm-password-text-field-input',
    jobTitleTextField: 'job-title-text-field',
    jobTitleTextFieldInput: 'job-title-text-field-input',
    workPhoneTextField: 'work-phone-text-field',
    workPhoneTextFieldInput: 'work-phone-text-field-input',
    workExtensionTextField: 'work-extension-text-field',
    workExtensionTextFieldInput: 'work-extension-text-field-input',
    mobilePhoneTextField: 'mobile-phone-text-field',
    mobilePhoneTextFieldInput: 'mobile-phone-text-field-input',
    prefferedContactTextFieldInput: 'preffered-contact-text-field-input',
    signupButton: 'sign-up-button',
    iAlreadyHaveALoginLink: 'already-have-a-login-link',
    togglePasswordVisibilityButton: 'toggle-password-visibility-button',
    toggleConfirmPasswordVisibilityButton:
        'toggle-confirm-password-visibility-button',
    invalidPasswordNotification: 'invalid-password-notification',
    errorNotification: 'error-user-notification',
    infoNotification: 'info-user-notification',
    preferredContactMethodEmail: 'preferred-contact-method-email',
    preferredContactMethodMobile: 'preferred-contact-method-mobile',
    preferredContactMethodWork: 'preferred-contact-method-work',
    resetPasswordLink: 'reset-password-link',
    signUpFormContainer: 'sign-up-form-container',
}

export interface UsersRegistrationFormData {
    email: string
    firstName: string
    lastName: string
    password: string
    confirmPassword: string
    jobTitle: string
    workPhone: string
    workExtension: string
    mobilePhone: string
    preferredContactMethod: PreferredContactMethod | undefined
}

export type PreferredContactMethod = 'Work' | 'Mobile' | 'Email'
